import React, { useCallback, useEffect, useState } from 'react';

function useOverflow(
  container: React.RefObject<HTMLElement> /* React.Ref<HTMLElement> */,
  updater: any // The thing that changes and triggers this to reevaluate
) {
  const [isRightOverflowing, setIsRightOverflowing] = useState<boolean>(false);
  const [isLeftOverflowing, setIsLeftOverflowing] = useState<boolean>(false);

  const [isTopOverflowing, setIsTopOverflowing] = useState<boolean>(false);
  const [isBottomOverflowing, setIsBottomOverflowing] =
    useState<boolean>(false);

  // Determine if overflowing or not based on current properties of element
  const handleScroll = useCallback(() => {
    const { current } = container;
    if (!current) return;

    // Handle X Overflow
    if (current.scrollLeft > 0) {
      setIsLeftOverflowing(true);
    } else {
      setIsLeftOverflowing(false);
    }

    const rightEdgeOfView = current.scrollLeft + current.clientWidth + 1;
    if (rightEdgeOfView <= current.scrollWidth) {
      setIsRightOverflowing(true);
    } else {
      setIsRightOverflowing(false);
    }

    // Handle Y Overflow
    const isYScroll = current.scrollHeight > current.clientHeight;

    if (current.scrollTop > 0) {
      setIsTopOverflowing(true);
    } else {
      setIsTopOverflowing(false);
    }

    const bottomEdgeOfView = current.scrollTop + current.clientHeight + 1;
    if (bottomEdgeOfView <= current.scrollHeight && isYScroll) {
      setIsBottomOverflowing(true);
    } else {
      setIsBottomOverflowing(false);
    }
  }, [container]);

  // Whenever scroll or the updater changes, this will rerun
  useEffect(() => {
    const { current } = container;
    if (!current) return () => {};

    handleScroll();

    current.addEventListener('scroll', handleScroll);

    return () => {
      current.removeEventListener('scroll', handleScroll);
    };
  }, [container, handleScroll, updater]);

  return {
    /** True if there is more content BELOW of the current view */
    isBottomOverflowing,
    /** True if there is more content LEFT of the current view */
    isLeftOverflowing,
    /** True if there is more content RIGHT of the current view */
    isRightOverflowing,
    /** True if there is more content ABOVE of the current view */
    isTopOverflowing,
  };
}

export default useOverflow;
